/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import isEmpty from 'lodash.isempty';
import dynamic from 'next/dynamic';
import Features, {ADMIN_LANDMARK_GEOFENCE_PAGE} from 'security/Features';
import {GenericObject, HVSRecord} from '@ion/core/src/types/entities';
import {EXTENT_FACTOR, mapViewGoToOptions} from '@ion/dls/src/maps/constants';
import {createExtentFromAVL} from '@ion/dls/src/maps/helpers/MapViewUtils';
import useAssetHistoryController from '@ion/dls/src/maps/hooks/useAssetHistoryController';
import {MapProps} from '@ion/dls/src/maps/types/types';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import useBottomSheetProps from '@/routes/tracking2/map/containers/hooks/useBottomSheetProps';
import {initializeOperationalLayers, resetSelectedAsset} from '@/store/actions/tracking';
import useWebMapId from '../hooks/useWebMapId';
import AssetHistoryLayerContainer from '../layers/AssetHistoryLayerContainer';
import BoundaryLayerContainer from '../layers/BoundaryLayerContainer';
import MapContextMenuLayerContainer from '../layers/MapContextMenuLayerContainer';
import OperationalLayerContainer from '../layers/OperationalLayerContainer';
import MapWidgetContainer from '../widgets/MapWidgetContainer';
import TimeModePanelContainer from '../widgets/TimeModePanelContainer';

type AssetHistoryMapProps = {
  assetsRoutesHistory?: GenericObject;
  queryParams?: GenericObject;
  featureFilter?: GenericObject;
  timeSliderDisabled?: boolean;
  showExitSelection?: boolean;
  renderMapWidgets?: boolean;
  isTripServicesEnabled?: boolean;
  dataLoaded?: boolean;
  onExitSelection?: Function;
  setHoveredEvent?: Function;
  onLayerDrawn?: Function;
  onToggleTimeSlider?: Function;
  onSetView?: Function;
  hoveredEvent?: HVSRecord;
  timeline?: any;
};

const DynamicAssetHistoryMap = dynamic(() => import('@ion/dls/src/maps/MapContainer'), {
  ssr : false
});

/**
 * AssetHistoryMap
 *
 * Map container for rendering Asset History visualization.
 *
 * Responsibilities:
 * - Initializes and configures the map (basemap, operational layers, webMapId)
 * - Renders asset routes, events, and boundaries via layer containers
 * - Integrates map widgets (time slider, geofence tools, controls)
 * - Connects map interactions with global state (Redux)
 *
 * Notes:
 * - Map is dynamically imported (no SSR) to avoid server-side rendering issues
 * - Uses memoized props to prevent unnecessary re-renders of the map
 * - Conditionally renders only when basemap is available
 */
const AssetHistoryMap = ({
  isTripServicesEnabled,
  assetsRoutesHistory,
  timeSliderDisabled,
  onToggleTimeSlider,
  showExitSelection,
  renderMapWidgets,
  onExitSelection,
  setHoveredEvent,
  featureFilter,
  onLayerDrawn,
  hoveredEvent,
  queryParams,
  dataLoaded,
  onSetView,
  timeline
}: AssetHistoryMapProps) => {
  const dispatch                                      = useDispatch();
  const webMapId                                      = useWebMapId();
  const [editGeofenceGraphic, setEditGeofenceGraphic] = useState(null);
  const canAddGeofence                                = Features.has(ADMIN_LANDMARK_GEOFENCE_PAGE);
  const {currentBasemap}                              = useBottomSheetProps();
  const {isCurrent: isCurrentTheme}                 = useThemeContext();

  const {getAllAssetsHistory} = useAssetHistoryController({} as any);
  const setOperationalLayers = useCallback(map => {
    dispatch(initializeOperationalLayers(map) as any);
  }, []);

  const onResetSelectedAsset = useCallback(() => {
    dispatch(resetSelectedAsset({isMapSelection: true}) as any);
  }, []);

  const mapProps: MapProps = useMemo(() => ({
    basemap                : currentBasemap,
    disableZoomingDblClick : false,
    isTracking             : false,
    setOperationalLayers,
    onResetSelectedAsset,
    webMapId
  }), [
    setOperationalLayers,
    onResetSelectedAsset,
    currentBasemap,
    webMapId
  ]);

  const initialExtent = useMemo(() => {
    if (!isEmpty(assetsRoutesHistory) && dataLoaded) {
      const extent = createExtentFromAVL(getAllAssetsHistory(assetsRoutesHistory));
      return extent?.expand(EXTENT_FACTOR);
    }
    return null;
  }, [assetsRoutesHistory, dataLoaded]);

  // the current theme's widget cluster Zoom to fit returns the map to the
  // history's own extent - read through refs since the widget container keeps
  // the first callback it receives ...
  const viewRef          = useRef<__esri.MapView>(null);
  const initialExtentRef = useRef(initialExtent);

  useEffect(function() {
    initialExtentRef.current = initialExtent;
  }, [initialExtent]);

  const onHistorySetView = useCallback(function(view: __esri.MapView) {
    viewRef.current = view;
    onSetView?.(view);
  }, [onSetView]);

  const onZoomToFit = useCallback(function() {
    if (viewRef.current && initialExtentRef.current) {
      viewRef.current.goTo(initialExtentRef.current, mapViewGoToOptions);
    }
  }, []);

  const mapWidgetProps = useMemo(() => {
    return {
      assetsRoutesHistory,
      timeSliderDisabled,
      onToggleTimeSlider,
      showExitSelection,
      onExitSelection,
      featureFilter,
      hoveredEvent,
      timeline
    };
  }, [
    assetsRoutesHistory,
    timeSliderDisabled,
    onToggleTimeSlider,
    showExitSelection,
    onExitSelection,
    featureFilter,
    hoveredEvent,
    timeline
  ]);

  if (currentBasemap) {
    return (
      <>
        <DynamicAssetHistoryMap key={`${webMapId}`}
          initialExtent={initialExtent}
          mapProps={mapProps}
          enableMapAutoCluster>
          <AssetHistoryLayerContainer assetsRoutesHistory={assetsRoutesHistory}
            isTripServicesEnabled={isTripServicesEnabled}
            queryParams={queryParams}
            initialExtent={initialExtent}
            hoveredEvent={hoveredEvent}
            onEventHovered={setHoveredEvent}
            onLayerDrawn={onLayerDrawn}
            timeline={timeline}
            onSetView={onHistorySetView} />
          <BoundaryLayerContainer />
          <MapContextMenuLayerContainer />
          <OperationalLayerContainer />
          {renderMapWidgets && isCurrentTheme && (<TimeModePanelContainer />)}
          {renderMapWidgets && (<MapWidgetContainer editGeofenceGraphic={editGeofenceGraphic}
            setEditGeofenceGraphic={setEditGeofenceGraphic}
            canAddGeofence={canAddGeofence}
            onZoomToFit={onZoomToFit}
            renderMapWidgets={renderMapWidgets}
            mapWidgetProps={mapWidgetProps}
            isAssetHistoryReport />
          )}
        </DynamicAssetHistoryMap>
      </>
    );
  }
};

export default AssetHistoryMap;
